import type { ReactNode } from "react";
import { ScrollView, View } from "react-native";
import {
  DialogBodyText,
  PrimaryTitleText,
  SupportingSubtitleText,
  colors,
} from "@louise/moon-ui";
import { HandHeart, Stethoscope, Users } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import { ArticleRailCard } from "../../../components/ArticleSectionCard";
import { getPostCycleArticles } from "../postCycleArticles";

type Props = {
  onOpenArticle?: (articleId: string) => void;
};

export function NegativeCloseoutSupport({ onOpenArticle }: Props) {
  const t = useTranslations();
  const articles = onOpenArticle
    ? getPostCycleArticles("negative", t("care.lab_result.tag_post_cycle"), t)
    : [];

  return (
    <>
      <DialogBodyText>{t("care.beta_result.negative_subhead")}</DialogBodyText>

      <View style={{ gap: 14 }}>
        <SupportBullet
          icon={
            <Stethoscope size={16} color={colors.warm} strokeWidth={1.75} />
          }
          label={t("care.beta_result.resource_care_team_label")}
          hint={t("care.beta_result.resource_care_team_hint")}
        />
        <SupportBullet
          icon={<HandHeart size={16} color={colors.warm} strokeWidth={1.75} />}
          label={t("care.beta_result.resource_self_care_label")}
          hint={t("care.beta_result.resource_self_care_hint")}
        />
        <SupportBullet
          icon={<Users size={16} color={colors.warm} strokeWidth={1.75} />}
          label={t("care.beta_result.resource_community_label")}
          hint={t("care.beta_result.resource_community_hint")}
        />
      </View>

      {articles.length > 0 && onOpenArticle ? (
        <View testID="post-cycle-article-rail">
          {articles.length === 1 && articles[0] ? (
            <ArticleRailCard
              article={articles[0]}
              read={false}
              onPress={() => onOpenArticle(articles[0]!.id)}
              fullWidth
            />
          ) : (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{
                gap: 12,
                paddingLeft: 28,
                paddingRight: 28,
              }}
              style={{ marginHorizontal: -28 }}
            >
              {articles.map((article) => (
                <ArticleRailCard
                  key={article.id}
                  article={article}
                  read={false}
                  onPress={() => onOpenArticle(article.id)}
                />
              ))}
            </ScrollView>
          )}
        </View>
      ) : null}
    </>
  );
}

function SupportBullet({
  icon,
  label,
  hint,
}: {
  icon: ReactNode;
  label: string;
  hint: string;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 10,
      }}
    >
      <View style={{ marginTop: 3 }}>{icon}</View>
      <View style={{ flex: 1, gap: 2 }}>
        <PrimaryTitleText style={{ fontSize: 14, lineHeight: 19 }}>
          {label}
        </PrimaryTitleText>
        <SupportingSubtitleText>{hint}</SupportingSubtitleText>
      </View>
    </View>
  );
}

export default NegativeCloseoutSupport;
